export const COOKIE_CONSENT_KEY = 'cookieConsent';
export const COOKIE_CONSENT_EVENT = 'cookieConsentChanged';
export const PURCHASE_DEDUPE_PREFIX = 'ga4:purchase:';
export const BOOKING_COMPLETED_DEDUPE_PREFIX = 'ga4:booking_completed:';

let initialized = false;
let lastPageViewPath = null;
let gtagScriptTimer = 0;
let gtagScriptIdle = 0;

const GTAG_INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll'];

function measurementId() {
  return (
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    process.env.REACT_APP_GOOGLE_ANALYTICS_ID ||
    (process.env.NODE_ENV === 'production' ? 'G-HXWELNSXEL' : null)
  );
}

function detachGtagTriggers() {
  if (gtagScriptIdle && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(gtagScriptIdle);
  }
  if (gtagScriptTimer) {
    window.clearTimeout(gtagScriptTimer);
  }
  gtagScriptIdle = 0;
  gtagScriptTimer = 0;
  if (typeof window !== 'undefined') {
    GTAG_INTERACTION_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, onGtagInteraction);
    });
  }
}

export function resetGoogleAnalyticsForTests() {
  initialized = false;
  lastPageViewPath = null;
  detachGtagTriggers();
}

export function hasAnalyticsConsent() {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted';
}

function readStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch (err) {
    return null;
  }
}

function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch (err) {
    return false;
  }
}

function injectGtagScript() {
  const id = measurementId();
  if (!id || !hasAnalyticsConsent() || document.getElementById('ga-gtag-script')) {
    return;
  }
  const script = document.createElement('script');
  script.id = 'ga-gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
}

function onGtagInteraction() {
  detachGtagTriggers();
  injectGtagScript();
}

// The dataLayer stub is synchronous so page_view and purchase calls queue
// immediately. The network script waits for the first interaction, or for
// idle (capped at 5s), whichever comes first.
function scheduleGtagScript() {
  if (document.getElementById('ga-gtag-script') || gtagScriptIdle || gtagScriptTimer) {
    return;
  }
  GTAG_INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onGtagInteraction, { passive: true, once: true });
  });
  const fire = () => {
    gtagScriptIdle = 0;
    gtagScriptTimer = 0;
    onGtagInteraction();
  };
  if (typeof window.requestIdleCallback === 'function') {
    gtagScriptIdle = window.requestIdleCallback(fire, { timeout: 5000 });
    return;
  }
  gtagScriptTimer = window.setTimeout(fire, 5000);
}

export function initGoogleAnalytics() {
  if (!measurementId() || !hasAnalyticsConsent() || initialized) {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window.gtag('js', new Date());
  window.gtag('config', measurementId(), { send_page_view: false });
  scheduleGtagScript();

  initialized = true;
  return true;
}

function canSend() {
  if (!measurementId() || !hasAnalyticsConsent()) {
    return false;
  }
  if (!initialized) {
    initGoogleAnalytics();
  }
  return initialized && typeof window.gtag === 'function';
}

function absolutePageLocation(pagePath) {
  if (/^https?:\/\//i.test(pagePath)) {
    return pagePath;
  }
  const origin = window.location.origin || '';
  const path = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
  return `${origin}${path}`;
}

/**
 * One page_view per path. send_page_view is false on the gtag config, so this
 * is the only page_view. The same path in a row is ignored so a second effect
 * pass cannot double-count; a later visit to that path still counts.
 */
export function trackPageView(path, title) {
  if (!canSend()) {
    return false;
  }

  const pagePath = path || `${window.location.pathname}${window.location.search}`;
  if (pagePath === lastPageViewPath) {
    return false;
  }

  const pageTitle = title || document.title;
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: absolutePageLocation(pagePath),
  });
  lastPageViewPath = pagePath;
  return true;
}

function finiteRupees(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
}

/**
 * GA4 commerce params. Value is rupees (not paise). Only the listed fields are
 * copied, so phone, name, and email on the input cannot be sent.
 */
export function commerceEventParams({
  transactionId,
  value,
  itemId,
  itemName,
  itemCategory,
} = {}) {
  const transaction_id = transactionId == null ? '' : String(transactionId).trim();
  const rupees = finiteRupees(value);
  if (!transaction_id || rupees == null || rupees < 0) {
    return null;
  }

  return {
    transaction_id,
    value: rupees,
    currency: 'INR',
    items: [
      {
        item_id: itemId == null ? '' : String(itemId),
        item_name: itemName == null ? '' : String(itemName),
        item_category: itemCategory == null ? '' : String(itemCategory),
      },
    ],
  };
}

export function purchaseInputFromBooking(booking) {
  if (!booking || typeof booking !== 'object') {
    return null;
  }
  const service = booking.serviceId;
  const serviceObject = service && typeof service === 'object' ? service : null;
  const itemId = serviceObject
    ? serviceObject.slug || serviceObject._id || serviceObject.id || ''
    : service;

  return {
    transactionId: booking._id == null ? '' : String(booking._id),
    value: booking.callOutFee,
    itemId: itemId == null ? '' : String(itemId),
    itemName: serviceObject?.name ? String(serviceObject.name) : '',
    itemCategory: booking.urgency == null ? '' : String(booking.urgency),
  };
}

export function bookingHasVerifiedPayment(booking) {
  if (!booking || typeof booking !== 'object') {
    return false;
  }
  if (booking.paymentStatus === 'paid' || booking.callOutFeePaid === true) {
    return true;
  }
  return booking.callOutFeePaymentId != null && String(booking.callOutFeePaymentId) !== '';
}

/** True when this paid booking was created during the current checkout window. */
export function isRecentVerifiedBooking(booking, now = Date.now(), maxAgeMs = 15 * 60 * 1000) {
  if (!bookingHasVerifiedPayment(booking)) {
    return false;
  }
  const createdAtMs = new Date(booking.createdAt).getTime();
  if (!Number.isFinite(createdAtMs)) {
    return false;
  }
  const ageMs = now - createdAtMs;
  return ageMs <= maxAgeMs && ageMs >= -60 * 1000;
}

function alreadySent(prefix, transactionId) {
  return readStorage(window.sessionStorage, `${prefix}${transactionId}`) === '1';
}

function markSent(prefix, transactionId) {
  writeStorage(window.sessionStorage, `${prefix}${transactionId}`, '1');
}

function sendCommerceEvent(eventName, prefix, input) {
  try {
    const params = commerceEventParams(input || {});
    if (!params || !canSend()) {
      return false;
    }
    if (alreadySent(prefix, params.transaction_id)) {
      return false;
    }
    window.gtag('event', eventName, params);
    markSent(prefix, params.transaction_id);
    return true;
  } catch (err) {
    return false;
  }
}

export function trackPurchase(input) {
  return sendCommerceEvent('purchase', PURCHASE_DEDUPE_PREFIX, input);
}

export function trackBookingCompleted(input) {
  return sendCommerceEvent('booking_completed', BOOKING_COMPLETED_DEDUPE_PREFIX, input);
}
